import type { StoreObject } from "@apollo/client";
import { useEffect } from "react";
import ModalCloseIcon from "@/components/icons/modal-close-icon";
import type { PlayedButtonProps } from "@/components/played-button/types";
import RateOptions from "@/components/rate-options";
import { Button } from "@/components/ui/button";
import useUpdateEscapeMutation, {
  convertEscapeToInput,
  type EscapeType,
  PrivacyEnum,
} from "@/graphql/mutations/use-update-escape";
import { cache } from "@/helpers/apollo";
import useAuth from "@/hooks/use-auth";
import type { OverallRatingEnum } from "@/pages/search/types";
import ReviewAsk from "./review-ask";

export const RatingStepsEnum = {
  DEFAULT: 0,
  RATING_STEP: 1,
  REVIEW_STEP: 2,
} as const;

interface PlayedButtonAltProps extends PlayedButtonProps {
  currentRatingStep: (typeof RatingStepsEnum)[keyof typeof RatingStepsEnum];
  setCurrentRatingStep: (
    step: (typeof RatingStepsEnum)[keyof typeof RatingStepsEnum]
  ) => void;
}

export default function PlayedButton({
  currentRatingStep,
  game,
  onRating = () => {
    /* No-op */
  },
  setCurrentRatingStep,
}: PlayedButtonAltProps) {
  const { mutationContext } = useAuth();
  const [updateEscape, { data: updateEscapeData }] = useUpdateEscapeMutation(
    mutationContext({ escape: { game: game.id } })
  );

  const handleRating = (rating: keyof typeof OverallRatingEnum) => {
    const overallRating = game.overallRating === rating ? undefined : rating;

    if (overallRating) {
      onRating(overallRating);
    }

    cache.modify({
      fields: {
        overallRating: () => overallRating as keyof typeof OverallRatingEnum,
      },
      id: cache.identify(game as unknown as StoreObject),
    });

    const variables = convertEscapeToInput(
      (updateEscapeData?.updateEscape.escape || game.myEscape) as EscapeType
    );

    if (variables.experienceReview) {
      variables.experienceReview.overallRating = overallRating;
    } else {
      variables.experienceReview = {
        overallRating,
        privacy: PrivacyEnum.PUBLIC,
      };
    }

    updateEscape(mutationContext(variables));
    setCurrentRatingStep(RatingStepsEnum.REVIEW_STEP);
  };

  useEffect(() => {
    if (updateEscapeData?.updateEscape.escape) {
      cache.modify({
        fields: {
          myEscape: () => updateEscapeData.updateEscape.escape as EscapeType,
        },
        id: cache.identify(game as unknown as StoreObject),
      });
    }
  }, [game, updateEscapeData]);

  const dismissRatingSteps = () =>
    setCurrentRatingStep(RatingStepsEnum.DEFAULT);

  return (
    <>
      {currentRatingStep === RatingStepsEnum.RATING_STEP && (
        <div>
          <span className="block pb-6 text-center font-semibold">
            How was {game.name}?
          </span>
          <RateOptions
            onClick={handleRating}
            overallRating={game.overallRating}
          />
        </div>
      )}
      {currentRatingStep === RatingStepsEnum.REVIEW_STEP && (
        <ReviewAsk dismissRatingSteps={dismissRatingSteps} game={game} />
      )}
      <Button
        className="absolute top-2.5 right-2.5 flex size-6 cursor-pointer items-center justify-center rounded-full bg-white/10 shadow-white/20 transition-box-shadow hover:shadow-0-0-10"
        onClick={dismissRatingSteps}
      >
        <ModalCloseIcon />
      </Button>
    </>
  );
}
